import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  alpha,
  styled,
  keyframes,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SearchIcon from "@mui/icons-material/Search";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOutUp = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "16px",
  background: alpha(theme.palette.background.paper, 0.6),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  marginBottom: theme.spacing(2),
  backdropFilter: "blur(10px)",
}));

const QuestionCardContainer = styled(Box)(({ theme, stage, isExiting }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  background: alpha(theme.palette.background.paper, 0.7),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  marginBottom: theme.spacing(1),
  animation: isExiting
    ? `${fadeOutUp} 0.3s ease-out forwards`
    : `${fadeInUp} 0.4s ease-out`,
  ...(stage === "thinking" && {
    borderColor: alpha(theme.palette.info.main, 0.3),
    background: alpha(theme.palette.info.main, 0.05),
    animation: `${fadeInUp} 0.4s ease-out, ${pulse} 2s ease-in-out infinite`,
  }),
  ...(stage === "searching" && {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    background: alpha(theme.palette.primary.main, 0.05),
    animation: `${fadeInUp} 0.4s ease-out, ${pulse} 2s ease-in-out infinite`,
  }),
  ...(stage === "understanding" && {
    borderColor: alpha(theme.palette.secondary.main, 0.3),
    background: alpha(theme.palette.secondary.main, 0.05),
    animation: `${fadeInUp} 0.4s ease-out, ${pulse} 2s ease-in-out infinite`,
  }),
  ...(stage === "complete" && {
    borderColor: alpha(theme.palette.success.main, 0.2),
    background: alpha(theme.palette.success.main, 0.05),
    opacity: 0.6,
  }),
}));

const StageIcon = styled(Box)(({ theme, stage }) => {
  const stageColors = {
    thinking: theme.palette.info.main,
    searching: theme.palette.primary.main,
    understanding: theme.palette.secondary.main,
    complete: theme.palette.success.main,
  };

  return {
    width: 28,
    height: 28,
    borderRadius: "8px",
    display: "grid",
    placeItems: "center",
    background: alpha(stageColors[stage] || theme.palette.grey[500], 0.15),
    color: stageColors[stage] || theme.palette.grey[500],
    fontSize: 14,
  };
});

// --- REMOVED QuestionBadge component as it's no longer used ---
// const QuestionBadge = styled(Box)(({ theme }) => ({ ... }));

function QuestionCard({
  question,
  questionIndex, // Kept in props in case logic needs it, but not displayed
  iteration, // Kept in props in case logic needs it, but not displayed
  stage,
  isExiting,
}) {
  const getStageIcon = () => {
    switch (stage) {
      case "thinking":
        return <PsychologyIcon sx={{ fontSize: 14 }} />;
      case "searching":
        return <SearchIcon sx={{ fontSize: 14 }} />;
      case "understanding":
        return <AutoStoriesIcon sx={{ fontSize: 14 }} />;
      case "complete":
        return <CheckCircleIcon sx={{ fontSize: 14 }} />;
      default:
        return null;
    }
  };

  const getStageText = () => {
    switch (stage) {
      case "thinking":
        return "thinking";
      case "searching":
        return "Searching";
      case "understanding":
        return "Analysing";
      case "complete":
        return "Comprehending";
      default:
        return "Generated";
    }
  };

  return (
    <QuestionCardContainer stage={stage} isExiting={isExiting}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <StageIcon stage={stage}>{getStageIcon()}</StageIcon>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
            {/* --- FIX 2: Removed QuestionBadge --- */}
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {getStageText()}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              fontSize: "13px",
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {question}
          </Typography>
        </Stack>
      </Stack>
    </QuestionCardContainer>
  );
}

export default function EnhancedProgressDisplay({
  progressUpdates = [],
  mode = "deeper_dive",
}) {
  const [questions, setQuestions] = useState([]);
  const [currentIteration, setCurrentIteration] = useState(0);
  const [exitingQuestions, setExitingQuestions] = useState(new Set());
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const { scrollHeight } = scrollContainerRef.current;
      scrollContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: "smooth",
      });
    }
  }, [questions]);

  useEffect(() => {
    if (!progressUpdates.length) return;

    const latest = progressUpdates[progressUpdates.length - 1];

    if (latest.subtype) {
      handleQuestionEvent(latest);
    }
  }, [progressUpdates]);

  const handleQuestionEvent = (event) => {
    const { subtype, question, questionIndex, stage } = event;

    let iteration = 0;
    if (stage) {
      const iterationMatch = stage.match(/iteration_(\d+)/);
      iteration = iterationMatch ? parseInt(iterationMatch[1], 10) : 0;
    }

    if (iteration === 0) {
      iteration = currentIteration;
    }

    const finalIteration =
      event.iteration !== undefined ? event.iteration : iteration;
    const finalQuestionIndex =
      event.questionIndex !== undefined ? event.questionIndex : 0;
    const questionId = `${finalIteration}-${finalQuestionIndex}`;

    switch (subtype) {
      case "question_generated":
        setQuestions((prev) => [
          ...prev,
          {
            id: questionId,
            question,
            questionIndex: finalQuestionIndex,
            iteration: finalIteration,
            stage: "generated",
          },
        ]);
        if (finalIteration > currentIteration) {
          setCurrentIteration(finalIteration);
        }
        break;

      case "thinking":
      case "searching":
      case "understanding":
        setQuestions((prev) => {
          const questionExists = prev.some((q) => q.id === questionId);

          if (questionExists) {
            return prev.map((q) =>
              q.id === questionId ? { ...q, stage: subtype } : q
            );
          } else {
            return [
              ...prev,
              {
                id: questionId,
                question: question,
                questionIndex: finalQuestionIndex,
                iteration: finalIteration,
                stage: subtype,
              },
            ];
          }
        });
        if (finalIteration > currentIteration) {
          setCurrentIteration(finalIteration);
        }
        break;

      case "question_complete":
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId ? { ...q, stage: "complete" } : q
          )
        );

        setTimeout(() => {
          setExitingQuestions((prev) => new Set(prev).add(questionId));

          setTimeout(() => {
            setQuestions((prev) => prev.filter((q) => q.id !== questionId));
            setExitingQuestions((prev) => {
              const next = new Set(prev);
              next.delete(questionId);
              return next;
            });
          }, 300);
        }, 2000);
        break;
    }
  };

  if (!progressUpdates.length && !questions.length) return null;

  return (
    <ProgressContainer>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Working on it...
            </Typography>
          </Box>

          {/* --- FIX 1: Removed Iteration count display --- */}
        </Stack>

        {questions.length > 0 && (
          <Box
            ref={scrollContainerRef}
            sx={{
              maxHeight: "220px",
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: "4px",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "10px",
              },
            }}
          >
            <Stack spacing={0.75}>
              {questions.map((q) => (
                <QuestionCard
                  key={q.id}
                  question={q.question}
                  questionIndex={q.questionIndex}
                  iteration={q.iteration}
                  stage={q.stage}
                  isExiting={exitingQuestions.has(q.id)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </ProgressContainer>
  );
}
